<template>
  <v-card class="mb-4">
    <v-card-title>購買履歴</v-card-title>

    <v-card-text>
      <v-skeleton-loader
        v-if="isLoading"
        type="card, table"
      />

      <template v-else>
        <!-- サマリー -->
        <v-row
          v-if="hasSummary"
          class="mb-4"
        >
          <v-col
            cols="6"
            sm="3"
          >
            <div class="text-subtitle-2 text--secondary">
              来店回数
            </div>
            <div class="text-h6">
              {{ summary.visitCount || 0 }}回
            </div>
          </v-col>
          <v-col
            cols="6"
            sm="3"
          >
            <div class="text-subtitle-2 text--secondary">
              総購入金額（大計）
            </div>
            <div class="text-h6">
              {{ formatCurrency(summary.totalPurchaseAmountWithTax || 0) }}
            </div>
            <div class="text-caption text--secondary">
              小計: {{ formatCurrency(summary.totalPurchaseAmount || 0) }}
            </div>
          </v-col>
          <v-col
            cols="6"
            sm="3"
          >
            <div class="text-subtitle-2 text--secondary">
              平均購入金額
            </div>
            <div class="text-h6">
              {{ formatCurrency(summary.averagePurchaseAmount || 0) }}
            </div>
          </v-col>
          <v-col
            cols="6"
            sm="3"
          >
            <div class="text-subtitle-2 text--secondary">
              最終来店日
            </div>
            <div class="text-h6">
              {{ formatDate(summary.lastVisitedAt) }}
            </div>
          </v-col>
        </v-row>

        <v-divider
          v-if="hasSummary"
          class="mb-4"
        />

        <!-- 購買明細 -->
        <template v-if="purchaseItems.length > 0 || totalCount > 0">
          <div class="text-subtitle-1 mb-2">
            購買明細
          </div>
          <v-data-table
            :options="tableOptions"
            :headers="headers"
            :items="purchaseItems"
            :loading="isLoading"
            :server-items-length="totalCount"
            :footer-props="{
              itemsPerPageText: '行/ページ:',
              itemsPerPageOptions: [10, 25, 50],
            }"
            no-data-text="データがありません"
            loading-text="読み込み中..."
            @update:options="handleOptionsUpdate"
          >
            <template #[`item.purchasedAt`]="{ item }">
              {{ formatDateTime(item.purchasedAt) }}
            </template>

            <template #[`item.tableName`]="{ item }">
              {{ item.tableName || '-' }}
            </template>

            <template #[`item.vendibleName`]="{ item }">
              {{ item.vendibleName || '-' }}
            </template>

            <template #[`item.sellingPrice`]="{ item }">
              <div>{{ formatCurrency(item.sellingPriceWithTax || 0) }}</div>
              <div class="text-caption text--secondary">
                {{ formatCurrency(item.sellingPrice || 0) }}
              </div>
            </template>
          </v-data-table>
        </template>

        <div
          v-else-if="!hasSummary"
          class="text-center text--secondary py-4"
        >
          購買履歴がありません
        </div>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import { computed, ref } from '@vue/composition-api'
import { format } from 'date-fns'

export default {
  name: 'CustomerPurchaseHistory',
  props: {
    purchaseHistory: {
      type: Object,
      default: () => ({}),
    },
    purchaseItems: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    totalCount: {
      type: Number,
      default: 0,
    },
    page: {
      type: Number,
      default: 1,
    },
    perPage: {
      type: Number,
      default: 10,
    },
  },
  setup(props, { emit }) {
    const tableOptions = ref({
      page: props.page,
      itemsPerPage: props.perPage,
    })

    const headers = computed(() => [
      {
        text: '購入日時',
        value: 'purchasedAt',
        sortable: false,
        width: '180',
      },
      {
        text: 'テーブル',
        value: 'tableName',
        sortable: false,
        width: '120',
      },
      {
        text: '商品名',
        value: 'vendibleName',
        sortable: false,
      },
      {
        text: '金額（大計/小計）',
        value: 'sellingPrice',
        sortable: false,
        width: '140',
        align: 'end',
      },
    ])

    const summary = computed(() => {
      return props.purchaseHistory.summary || {}
    })

    const hasSummary = computed(() => {
      return props.purchaseHistory && props.purchaseHistory.summary
    })

    const handleOptionsUpdate = newOptions => {
      // 値が変わっていない場合は emit しない（無限ループ防止）
      if (
        tableOptions.value.page === newOptions.page
        && tableOptions.value.itemsPerPage === newOptions.itemsPerPage
      ) {
        return
      }
      tableOptions.value = newOptions
      emit('update:pagination', {
        page: newOptions.page,
        perPage: newOptions.itemsPerPage,
      })
    }

    const formatCurrency = value => {
      if (value === null || value === undefined) return '-'

      return `¥${Number(value).toLocaleString()}`
    }

    const formatDate = dateString => {
      if (!dateString) return '-'
      try {
        return format(new Date(dateString), 'yyyy/MM/dd')
      } catch (e) {
        return dateString
      }
    }

    const formatDateTime = dateString => {
      if (!dateString) return '-'
      try {
        return format(new Date(dateString), 'yyyy/MM/dd HH:mm')
      } catch (e) {
        return dateString
      }
    }

    return {
      headers,
      tableOptions,
      summary,
      hasSummary,
      handleOptionsUpdate,
      formatCurrency,
      formatDate,
      formatDateTime,
    }
  },
}
</script>
